// components/common/Button/Button.tsx
import { ReactNode } from 'react';
import { Button as MuiButton, CircularProgress, ButtonProps as MuiButtonProps } from '@mui/material';
import { styled, alpha, Theme } from '@mui/material/styles';

// Extended props interface
interface StyledButtonProps {
  glow?: boolean;
  outline?: boolean;
}

// Helper function to get palette color safely
type PaletteColorName = 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';
const getPaletteColor = (theme: Theme, color: string | undefined): string => {
  const paletteColors: PaletteColorName[] = ['primary', 'secondary', 'error', 'warning', 'success', 'info'];
  if (color && paletteColors.includes(color as PaletteColorName)) {
    return theme.palette[color as PaletteColorName].main;
  }
  return theme.palette.primary.main;
};

const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !['glow', 'outline'].includes(prop as string),
})<StyledButtonProps>(({ theme, color, glow, outline }) => {
  const paletteColor = getPaletteColor(theme, color);

  return {
    fontFamily: '"Rajdhani", sans-serif',
    fontWeight: 600,
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',

    '&:active': {
      transform: 'translateY(0)',
    },

    // Outline variant
    ...(outline && {
      backgroundColor: 'transparent',
      border: `1px solid ${paletteColor}`,
      color: paletteColor,
      '&:hover': {
        backgroundColor: alpha(paletteColor, 0.1),
        borderColor: paletteColor,
      },
    }),

    // Glow effect on hover
    ...(glow && {
      '&:hover': {
        boxShadow: `0 0 20px ${alpha(paletteColor, 0.4)}`,
      },
    }),
  };
});

type ButtonColor = 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info' | 'inherit';
type ButtonVariant = 'contained' | 'outlined' | 'text' | 'ghost';

export interface ButtonProps extends Omit<MuiButtonProps, 'color' | 'variant'> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  glow?: boolean;
  outline?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  children: ReactNode;
}

/**
 * Custom Button component with RoundMaster styling
 */
export function Button({
  children,
  variant = 'contained',
  color = 'primary',
  glow = false,
  outline = false,
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  ...props
}: ButtonProps) {
  // Map 'ghost' variant to 'text' for MUI
  const muiVariant = variant === 'ghost' ? 'text' : variant;

  return (
    <StyledButton
      variant={outline ? 'outlined' : muiVariant}
      color={color}
      glow={glow}
      outline={outline}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : startIcon}
      endIcon={endIcon}
      {...props}
    >
      {children}
    </StyledButton>
  );
}

// Preset button variants for convenience
export function PrimaryButton(props: Omit<ButtonProps, 'color' | 'glow'>) {
  return <Button color="primary" glow {...props} />;
}

export function DangerButton(props: Omit<ButtonProps, 'color' | 'glow'>) {
  return <Button color="error" glow {...props} />;
}

export function SuccessButton(props: Omit<ButtonProps, 'color' | 'glow'>) {
  return <Button color="success" glow {...props} />;
}

export function WarningButton(props: Omit<ButtonProps, 'color' | 'glow'>) {
  return <Button color="warning" glow {...props} />;
}

export function GhostButton(props: Omit<ButtonProps, 'variant'>) {
  return <Button variant="text" {...props} />;
}

interface OutlineButtonProps extends Omit<ButtonProps, 'outline'> {
  color?: ButtonColor;
}

export function OutlineButton({ color = 'primary', ...props }: OutlineButtonProps) {
  return <Button outline color={color} {...props} />;
}

export default Button;
